import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  setDoc,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";
import { PinData, PinType } from "../types";
import { PIN_CONFIG } from "../constants";

export class PinService {
  private unsubscribe: (() => void) | null = null;
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;
  private pins: PinData[] = [];
  private listeners: ((pins: PinData[]) => void)[] = [];

  /**
   * ピンの変更をリアルタイム購読
   */
  subscribe(mapId: string): () => void {
    const q = query(collection(db, "pins"), orderBy("createdAt"));
    this.unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        this.pins = snapshot.docs
          .map((doc) => doc.data() as PinData)
          .filter((pin) => pin.mapId === mapId);

        this.notifyListeners();
      },
      (error) => {
        console.error("Failed to subscribe to pins:", error);
        if (error instanceof Error) {
          if (error.message.includes("Permission denied")) {
            console.warn(
              "⚠️ Firebase permission error. Please update Firestore security rules."
            );
          }
        }
      }
    );

    // 定期的に期限切れのピンを削除
    this.cleanupInterval = setInterval(() => {
      this.removeExpiredPins().catch((error) => {
        console.error("Failed to cleanup expired pins:", error);
      });
      this.notifyListeners();
    }, PIN_CONFIG.CLEANUP_INTERVAL_MS);

    return () => this.unsubscribe?.();
  }

  /**
   * ピンの変更をObserver パターンで監視
   */
  onChange(callback: (pins: PinData[]) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((listener) => listener !== callback);
    };
  }

  /**
   * 全リスナーに通知
   */
  private notifyListeners(): void {
    const activePins = this.getActivePins();
    this.listeners.forEach((listener) => listener(activePins));
  }

  /**
   * 有効期限内のピンのみ取得
   */
  private getActivePins(): PinData[] {
    const now = Date.now();
    return this.pins.filter(
      (pin) => now - pin.createdAt < PIN_CONFIG.MAX_DURATION_SECONDS * 1000
    );
  }

  /**
   * 期限切れのピンを削除
   */
  private async removeExpiredPins(): Promise<void> {
    const now = Date.now();
    const expiredPins = this.pins.filter(
      (pin) => now - pin.createdAt >= PIN_CONFIG.MAX_DURATION_SECONDS * 1000
    );

    await Promise.all(
      expiredPins.map((pin) =>
        deleteDoc(doc(db, "pins", pin.id)).catch((error) => {
          console.error(`Failed to delete pin ${pin.id}:`, error);
        })
      )
    );
  }

  /**
   * ピンを追加
   */
  async addPin(
    xRatio: number,
    yRatio: number,
    type: PinType,
    mapId: string
  ): Promise<void> {
    const pin: PinData = {
      id: Date.now().toString(),
      mapId,
      xRatio,
      yRatio,
      tag: type,
      createdAt: Date.now(),
    };

    try {
      await setDoc(doc(db, "pins", pin.id), pin);
    } catch (error) {
      console.error("Failed to add pin:", error);
      throw error;
    }
  }

  /**
   * ピンを削除
   */
  async removePin(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, "pins", id));
    } catch (error) {
      console.error("Failed to remove pin:", error);
      throw error;
    }
  }

  /**
   * リソースをクリーンアップ
   */
  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.listeners = [];
    this.pins = [];
  }

  /**
   * 現在のピンを取得（テスト用）
   */
  getPins(): PinData[] {
    return [...this.getActivePins()];
  }
}
